import { html, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, state, property } from 'lit/decorators.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import type { Risk, RiskPeriod, Period } from './risk.model';
import { Constants } from './risk.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import { dateShort } from '../../common/utils/formatDate';
import { RiskFragments } from '../../common/fragments/risk.fragments';
import { toLowerCase } from '../../common/utils/textFormat';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import style from './risk.scss?inline';

/**
 * @tag pop-risk-component
 * @property {Risk} [componentObject] Risk content
 * @attribute {Risk} [data-component-object] Risk content
 */
@customElement(Constants.TAG_NAME)
export class PopRiskComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: Risk;

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @state()
  period: Period = Constants.DEFAULT_PERIOD;

  /**
   * @internal
   */
  componentName = PopRiskComponent.tagName;

  /**
   * @internal
   */
  fragment = RiskFragments.riskFragment;

  constructor() {
    super(new ApiService());
  }

  initialize() {
    super.initialize();

    const { risks } = this.componentObject || {};
    const availableDataPeriods = this.getAvailableDataPeriods();

    if (risks?.length && !availableDataPeriods?.includes(this.period)) {
      this.period = availableDataPeriods[0];
    }
  }

  getAvailableDataPeriods(): Period[] {
    const periods = [
      ...(this.componentObject?.risks?.map((risk) => risk.timePeriodName) ??
        []),
    ];

    if (!periods || periods.length === 0) {
      return [];
    }

    return periods.sort(
      (a, b) => Constants.PERIODS.indexOf(a) - Constants.PERIODS.indexOf(b),
    );
  }

  private getSelectedRiskPeriodData(): RiskPeriod | undefined {
    return this.componentObject?.risks?.find(
      (risk) => risk.timePeriodName === this.period,
    );
  }

  private isRiskPeriod(value: string): value is Period {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (Constants.PERIODS as unknown as string[]).includes(value);
  }

  protected processResponse(_response): void {
    this.componentObject = _response?.riskReturnCharacteristicsExhibit;
  }

  private periodChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value;
    if (value && this.isRiskPeriod(value)) {
      this.period = value;
    } else {
      console.warn(`Invalid period value: ${value}`);
    }
  }

  renderTopSection() {
    return html`
      <h3
        class="risk-heading b-margin-block-end--micro--normal b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}
      </h3>
    `;
  }

  renderMiddleSection() {
    return html`
      ${!this.componentObject?.risks?.length
        ? html`<pop-no-data-available-component
            .message="${Constants.RISK_UNAVAILABLE}"
          ></pop-no-data-available-component>`
        : html`
            <div class="risk-middle-section">
              <p class="b-typography-sans-serif--caption-loose--300">
                ${this.componentObject?.portfolioName}
                ${Constants.AS_OF_DATE_PREFIX}
                ${dateShort(this.componentObject?.effectiveDate)}
              </p>
              ${this.renderControls()}
            </div>
          `}
    `;
  }

  renderControls() {
    const availableDataPeriods = this.getAvailableDataPeriods();

    if (availableDataPeriods.length === 1) {
      return html`<p class="b-typography-sans-serif--caption-loose--300">
        ${Constants.PERIOD_NO_DROPDOWN_PREFIX_LABEL} ${toLowerCase(this.period)}
        ${Constants.PERIOD_NO_DROPDOWN_POSTFIX_LABEL}
      </p>`;
    }

    if (availableDataPeriods.length > 1) {
      return html` <beacon-control>
        <label slot="label" for="period-select"
          >${Constants.PERIOD_DROPDOWN_HEADER_LABEL}</label
        >
        <select id="period-select" @change="${this.periodChangeHandler}">
          ${availableDataPeriods.map((period) => {
            return html`
              <option value="${period}" ?selected=${period === this.period}>
                ${toLowerCase(period)}
              </option>
            `;
          })}
        </select>
      </beacon-control>`;
    }

    return html``;
  }

  renderTable() {
    return html`
      <pop-risk-table-component
        data-component-object="${JSON.stringify({
          productName: this.componentObject?.portfolioName,
          risks: this.getSelectedRiskPeriodData()?.values,
          riskFactorGlossary: this.componentObject?.riskFactorGlossary,
        })}"
      ></pop-risk-table-component>
    `;
  }

  renderBenchmarkName() {
    return html`<p class="risk-benchmark">
      <span
        class="b-typography-sans-serif--caption-loose--300 b-font-weight-bold"
        >${Constants.BENCHMARK_PREFIX}</span
      >
      <span
        class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
        >${this.componentObject?.benchmarkName}</span
      >
    </p>`;
  }

  private renderDisclosures() {
    const disclaimers = this.componentObject?.disclaimers;
    if (!disclaimers || disclaimers.length === 0) {
      return html``;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  render() {
    return this.renderComponent(html`
      <section class="risk-content b-container">
        ${this.renderTopSection()} ${this.renderMiddleSection()}
        ${this.componentObject?.risks?.length
          ? html`${this.renderTable()} ${this.renderBenchmarkName()}
            ${this.renderDisclosures()}`
          : ''}
      </section>
    `);
  }
}
